import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProductDetail, updateProduct } from '../config/api';
import { toast } from 'react-toastify';
import useIsMobile from '../hooks/useIsMobile';

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [editPriceAccess, setEditPriceAccess] = useState(false);
  const [bulkSpecMode, setBulkSpecMode] = useState(false);
  const [bulkSpecText, setBulkSpecText] = useState('');

  useEffect(() => {
    // Check if user has superAdmin role
    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    if (userData.role !== 'superAdmin' && !userData.access?.includes("marketing")) {
      // Redirect to orders page if not superAdmin
      window.location.href = '/orders';
      return;
    }
    if(userData && userData.access?.includes('pricing')) {
      setEditPriceAccess(true);
    }
    
    loadProductDetail();
  }, [productId]);

  const loadProductDetail = async () => {
    setLoading(true);
    try {
      const response = await getProductDetail(productId);
      const productData = response.data || {};
      if(productData.error || !productData.id) {
        setProduct(null);
        console.error('Error loading product detail:', productData.error);
        toast.error('Failed to load product details');
        return;
      }
      setProduct(productData);
      setEditData({
        ...productData,
        specifications: Object.entries(productData.specifications || {}).map(([key, value]) => ({ key, value }))
      });
    } catch (error) {
      console.error('Error loading product detail:', error);
      toast.error('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const categories = {
      1: 'Solar Panels',
      2: 'Inverters',
      3: 'BOS',
      4: 'Kit',
      5: 'Battery'
    };
    return categories[categoryId] || 'Unknown';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditData({
      ...editData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      // Validate file type - allow PDF for data_sheet, images for others
      if (fieldName === 'data_sheet') {
        if (file.type !== 'application/pdf') {
          toast.error('Only PDF files are allowed for data sheet');
          return;
        }
      } else {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
          toast.error('Only JPEG and PNG images are allowed');
          return;
        }
      }

      setEditData({
        ...editData,
        [fieldName]: file
      });
    }
  };

  const handleMultipleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate each file
    for (let file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Each file size must be less than 5MB');
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only JPEG and PNG images are allowed');
        return;
      }
    }

    setEditData({
      ...editData,
      images: files
    });
  };

  const handleDiscountPriceChange = (index, field, value) => {
    const updatedPrices = [...editData.discount_price];
    updatedPrices[index] = {
      ...updatedPrices[index],
      [field]: field === 'price' || field === 'max' || field === 'qty' ? parseFloat(value) || '' : value
    };
    setEditData({
      ...editData,
      discount_price: updatedPrices
    });
  };

  const addDiscountPriceSlab = () => {
    setEditData({
      ...editData,
      discount_price: [...editData.discount_price, { max: '', qty: '', price: '' }]
    });
  };

  const removeDiscountPriceSlab = (index) => {
    if (editData.discount_price.length > 1) {
      const updatedPrices = editData.discount_price.filter((_, i) => i !== index);
      setEditData({
        ...editData,
        discount_price: updatedPrices
      });
    }
  };

  const handleSpecificationChange = (index, field, value) => {
    const updatedSpecs = [...editData.specifications];
    updatedSpecs[index] = {
      ...updatedSpecs[index],
      [field]: value
    };
    setEditData({
      ...editData,
      specifications: updatedSpecs
    });
  };

  const addSpecification = () => {
    setEditData({
      ...editData,
      specifications: [...editData.specifications, { key: '', value: '' }]
    });
  };

  const removeSpecification = (index) => {
    if (editData.specifications.length > 1) {
      const updatedSpecs = editData.specifications.filter((_, i) => i !== index);
      setEditData({
        ...editData,
        specifications: updatedSpecs
      });
    }
  };

  const toggleBulkSpecMode = () => {
    if (!bulkSpecMode) {
      // Convert current specifications to bulk text format
      const bulkText = editData.specifications
        .filter(spec => spec.key && spec.value)
        .map(spec => `${spec.key}:${spec.value}`)
        .join('\n');
      setBulkSpecText(bulkText);
    } else {
      // Convert bulk text back to specifications array
      const lines = bulkSpecText.split('\n').filter(line => line.trim());
      const newSpecs = [];
      
      lines.forEach(line => {
        const colonIndex = line.indexOf(':');
        if (colonIndex > 0) {
          const key = line.substring(0, colonIndex).trim();
          const value = line.substring(colonIndex + 1).trim();
          if (key && value) {
            newSpecs.push({ key, value });
          }
        }
      });
      
      // Ensure at least one empty specification if none were parsed
      if (newSpecs.length === 0) {
        newSpecs.push({ key: '', value: '' });
      }
      
      setEditData({
        ...editData,
        specifications: newSpecs
      });
    }
    setBulkSpecMode(!bulkSpecMode);
  };

  const handleBulkSpecTextChange = (e) => {
    setBulkSpecText(e.target.value);
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setBulkSpecMode(false);
    setBulkSpecText('');
    // Reset edit data to original product data
    setEditData({
      ...product,
      specifications: Object.entries(product.specifications || {}).map(([key, value]) => ({ key, value }))
    });
  };

  const handleSave = async () => {
    // Validation
    if (!editData.name.trim()) {
      toast.error('Product name is required');
      return;
    }
    
    if (!editData.price || editData.price <= 0) {
      toast.error('Valid price is required');
      return;
    }
    
    if (editData.category_id === 1 && (!editData.wattage || editData.wattage <= 0)) {
      toast.error('Wattage is mandatory for Solar Panels');
      return;
    }

    setSaving(true);
    try {
      // Prepare form data for file upload
      const formData = new FormData();
      
      // Add basic fields
      formData.append('category_id', editData.category_id);
      formData.append('name', editData.name);
      formData.append('description', editData.description);
      formData.append('price', editData.price);
      formData.append('warranty', editData.warranty);
      formData.append('brand', editData.brand);
      formData.append('wattage', editData.wattage);
      formData.append('skuCode', editData.skuCode);
      formData.append('short_name', editData.short_name || '');
      formData.append('tags', editData.tags || '');
      formData.append('dcr_applicable', editData.dcr_applicable || false);
      formData.append('hsn_code', editData.hsn_code || '');
      
      // Convert specifications array to object
      const specsObject = {};
      editData.specifications.forEach(spec => {
        if (spec.key && spec.value) {
          specsObject[spec.key] = spec.value;
        }
      });
      formData.append('specifications', JSON.stringify(specsObject));
      
      // Add files only if they are new File objects
      if (editData.thumbnail_image instanceof File) {
        formData.append('thumbnail_image', editData.thumbnail_image);
      }
      if (editData.original_image instanceof File) {
        formData.append('original_image', editData.original_image);
      }
      
      // Add multiple images if they are new File objects
      if (editData.images && Array.isArray(editData.images)) {
        editData.images.forEach((image, index) => {
          if (image instanceof File) {
            formData.append('images', image);
          }
        });
      }

      // Add data sheet if it's a new file
      if (editData.data_sheet instanceof File) {
        formData.append('data_sheet', editData.data_sheet);
      }

      const response = await updateProduct(productId, formData);
      
      if (response.success) {
        toast.success('Product updated successfully!');
        setEditing(false);
        loadProductDetail(); // Reload the product data
      } else {
        toast.error(response.message || 'Failed to update product');
      }
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#495057' }}>
            Loading product details...
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
          <h1 style={{ color: '#dc3545', marginBottom: '16px' }}>Product Not Found</h1>
          <p style={{ color: '#6c757d', marginBottom: '30px', fontSize: '16px' }}>
            The product with ID <strong>{productId}</strong> could not be found.
          </p>
          <Link to="/products" className="btn btn-primary">
            ← Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div className="order-detail-header">
          <div className="order-detail-title">
            <div className="breadcrumb">
              <Link to="/products">Products</Link> / Product Details
            </div>
            <h1>{product.name}</h1>
          </div>
          
          <div className="order-actions">
            <Link to="/products" className="btn btn-secondary">
              ← Back to Products
            </Link>
            {!editing ? (
              <button
                onClick={handleEdit}
                className="btn btn-primary"
              >
                Edit Product
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-success"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Basic Information */}
        <div className="card">
          <h3 className="section-title">Basic Information</h3>
          <div className="order-info">
            <div className="info-item">
              <span className="info-label">Category</span>
              {editing ? (
                <select
                  name="category_id"
                  className="form-control"
                  value={editData.category_id}
                  onChange={handleInputChange}
                >
                  <option value={1}>Solar Panels</option>
                  <option value={2}>Inverters</option>
                  <option value={3}>BOS</option>
                  <option value={4}>Kit</option>
                  <option value={5}>Battery</option>
                </select>
              ) : (
                <span className="info-value">{getCategoryName(product.category_id)}</span>
              )}
            </div>
            
            <div className="info-item">
              <span className="info-label">Product Name</span>
              {editing ? (
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={editData.name}
                  onChange={handleInputChange}
                />
              ) : (
                <span className="info-value">{product.name}</span>
              )}
            </div>
            
            <div className="info-item">
              <span className="info-label">Brand</span>
              {editing ? (
                <input
                  type="text"
                  name="brand"
                  className="form-control"
                  value={editData.brand}
                  onChange={handleInputChange}
                />
              ) : (
                <span className="info-value">{product.brand}</span>
              )}
            </div>
            
            <div className="info-item">
              <span className="info-label">SKU Code</span>
              {editing ? (
                <input
                  type="text"
                  name="skuCode"
                  className="form-control"
                  value={editData.skuCode}
                  onChange={() => {}}
                  disabled={true}
                />
              ) : (
                <span className="info-value">{product.skuCode || 'N/A'}</span>
              )}
            </div>

            <div className="info-item">
              <span className="info-label">HSN Code</span>
              {editing ? (
                <input
                  type="text"
                  name="hsn_code"
                  className="form-control"
                  value={editData.hsn_code || ''}
                  onChange={handleInputChange}
                  placeholder="Enter HSN code"
                  inputMode="numeric"
                />
              ) : (
                <span className="info-value">{product.hsn_code || 'N/A'}</span>
              )}
            </div>
            
            {(product.category_id === 1 || editData.category_id === 1) && (
              <div className="info-item">
                <span className="info-label">Wattage</span>
                {editing ? (
                  <input
                    type="number"
                    name="wattage"
                    className="form-control"
                    value={editData.wattage}
                    onChange={handleInputChange}
                    min="1"
                  />
                ) : (
                  <span className="info-value">{product.wattage ? `${product.wattage}W` : 'N/A'}</span>
                )}
              </div>
            )}
            
            {/* <div className="info-item">
              <span className="info-label">Price</span>
              {editing ? (
                <input
                  type="number"
                  name="price"
                  className="form-control"
                  value={editData.price}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                />
              ) : (
                <span className="info-value" style={{ fontSize: '20px', color: '#0d6efd' }}>
                  {formatPrice(product.price)}
                </span>
              )}
            </div> */}
            
            <div className="info-item">
              <span className="info-label">Warranty</span>
              {editing ? (
                <input
                  type="text"
                  name="warranty"
                  className="form-control"
                  value={editData.warranty}
                  onChange={handleInputChange}
                />
              ) : (
                <span className="info-value">{product.warranty || 'N/A'}</span>
              )}
            </div>

            <div className="info-item">
              <span className="info-label">Short Name</span>
              {editing ? (
                <input
                  type="text"
                  name="short_name"
                  className="form-control"
                  value={editData.short_name || ''}
                  onChange={handleInputChange}
                  placeholder="e.g., SP-500W"
                />
              ) : (
                <span className="info-value">{product.short_name || 'N/A'}</span>
              )}
            </div>

            <div className="info-item">
              <span className="info-label">Tags</span>
              {editing ? (
                <input
                  type="text"
                  name="tags"
                  className="form-control"
                  value={editData.tags || ''}
                  onChange={handleInputChange}
                  placeholder="Comma separated tags (e.g., solar, panel, 500w)"
                />
              ) : (
                <span className="info-value">{product.tags || 'N/A'}</span>
              )}
            </div>

            <div className="info-item">
              <span className="info-label">DCR</span>
              {editing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    name="dcr_applicable"
                    checked={editData.dcr_applicable || false}
                    onChange={handleInputChange}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <span style={{ color: '#6c757d' }}></span>
                </div>
              ) : (
                <span className="info-value">{product.dcr_applicable ? 'Yes' : 'No'}</span>
              )}
            </div>
          </div>
          
          <div className="info-item" style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
            <span className="info-label">Description</span>
            {editing ? (
              <textarea
                name="description"
                className="form-control"
                value={editData.description}
                onChange={handleInputChange}
                rows="4"
                style={{ resize: 'vertical' }}
              />
            ) : (
              <div className="address-card">
                <p>{product.description || 'No description available'}</p>
              </div>
            )}
          </div>
        </div>

        {/* Images */}
        <div className="card">
          <h3 className="section-title">Product Images</h3>
          <div className="order-info">
            <div className="info-item">
              <span className="info-label">Thumbnail Image</span>
              {editing ? (
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'thumbnail_image')}
                />
              ) : (
                product.thumbnail_image ? (
                  <img 
                    src={product.thumbnail_image} 
                    alt="Thumbnail" 
                    style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', borderRadius: '6px' }}
                  />
                ) : (
                  <span className="info-value">No image</span>
                )
              )}
            </div>
            
            <div className="info-item">
              <span className="info-label">Original Image</span>
              {editing ? (
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'original_image')}
                />
              ) : (
                product.original_image ? (
                  <img 
                    src={product.original_image} 
                    alt="Original" 
                    style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', borderRadius: '6px' }}
                  />
                ) : (
                  <span className="info-value">No image</span>
                )
              )}
            </div>
          </div>
          
          <div style={{ marginTop: '20px' }}>
            <span className="info-label">Additional Images</span>
            {editing ? (
              <div style={{ marginTop: '10px' }}>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  multiple
                  onChange={handleMultipleImagesChange}
                />
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                  Select multiple images. Max size: 5MB each, Format: JPEG, PNG
                </small>
                {editData.images && editData.images.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <div style={{ fontWeight: '500', marginBottom: '10px', color: '#495057' }}>
                      Selected Files: {editData.images.length} image(s)
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {Array.from(editData.images).map((file, index) => (
                        <div key={index} style={{ 
                          padding: '8px 12px', 
                          backgroundColor: '#f8f9fa', 
                          border: '1px solid #e9ecef', 
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#495057'
                        }}>
                          {file.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              product.images && product.images.length > 0 ? (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                  {product.images.map((image, index) => (
                    <img 
                      key={index}
                      src={image} 
                      alt={`Product ${index + 1}`} 
                      style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '6px' }}
                    />
                  ))}
                </div>
              ) : (
                <div style={{ marginTop: '10px', color: '#6c757d', fontStyle: 'italic' }}>
                  No additional images
                </div>
              )
            )}
          </div>
        </div>

        {/* Data Sheet */}
        <div className="card">
          <h3 className="section-title">Data Sheet</h3>
          <div className="info-item">
            {editing ? (
              <div>
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf"
                  onChange={(e) => handleFileChange(e, 'data_sheet')}
                />
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                  Upload PDF data sheet for this product
                </small>
                {product.documents?.['Data Sheet'] && (
                  <div style={{ marginTop: '10px' }}>
                    <span style={{ fontSize: '12px', color: '#6c757d' }}>Current: </span>
                    <a href={product.documents['Data Sheet']} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px' }}>
                      View existing data sheet
                    </a>
                  </div>
                )}
              </div>
            ) : (
              product.documents?.['Data Sheet'] ? (
                <a href={product.documents['Data Sheet']} target="_blank" rel="noopener noreferrer" className="btn btn-info" style={{ fontSize: '12px' }}>
                  📄 View Data Sheet
                </a>
              ) : (
                <span style={{ color: '#6c757d', fontStyle: 'italic' }}>No data sheet uploaded</span>
              )
            )}
          </div>
        </div>

        {/* Specifications */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 className="section-title" style={{ marginBottom: '0' }}>Specifications</h3>
            {editing && (
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={toggleBulkSpecMode}
                  className="btn btn-secondary"
                  style={{ fontSize: '12px', padding: '8px 16px' }}
                >
                  {bulkSpecMode ? '📝 Individual Edit' : '📋 Bulk Edit'}
                </button>
                {!bulkSpecMode && (
                  <button
                    type="button"
                    onClick={addSpecification}
                    className="btn btn-primary"
                    style={{ fontSize: '12px', padding: '8px 16px' }}
                  >
                    + Add Specification
                  </button>
                )}
              </div>
            )}
          </div>
          
          {editing ? (
            bulkSpecMode ? (
              <div>
                <div style={{ 
                  marginBottom: '15px', 
                  padding: '12px', 
                  backgroundColor: '#e7f3ff', 
                  border: '1px solid #b3d9ff', 
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#0056b3'
                }}>
                  <strong>💡 Bulk Edit Instructions:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>Enter each specification on a new line</li>
                    <li>Use format: <code style={{ backgroundColor: '#fff', padding: '2px 4px', borderRadius: '3px' }}>Key:Value</code></li>
                    <li>Example: <code style={{ backgroundColor: '#fff', padding: '2px 4px', borderRadius: '3px' }}>Power Output:540W</code></li>
                    <li>Empty lines will be ignored</li>
                  </ul>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Specifications (Bulk Edit)</label>
                  <textarea
                    className="form-control"
                    value={bulkSpecText}
                    onChange={handleBulkSpecTextChange}
                    rows="12"
                    placeholder="Enter specifications in format:&#10;Key1:Value1&#10;Key2:Value2&#10;Key3:Value3"
                    style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '14px',
                      resize: 'vertical',
                      lineHeight: '1.5'
                    }}
                  />
                </div>
                
                <div style={{ 
                  marginTop: '10px', 
                  padding: '10px', 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #e9ecef', 
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#6c757d'
                }}>
                  <strong>Preview:</strong> {bulkSpecText.split('\n').filter(line => line.trim() && line.includes(':')).length} specifications will be created
                </div>
              </div>
            ) : (
              editData.specifications.map((spec, index) => (
                <div key={index} className="order-info" style={{ marginBottom: '15px', padding: '15px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
                  <div className="info-item">
                    <label className="info-label">Specification Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={spec.key}
                      onChange={(e) => handleSpecificationChange(index, 'key', e.target.value)}
                      placeholder="e.g., Power Output"
                    />
                  </div>
                  
                  <div className="info-item">
                    <label className="info-label">Value</label>
                    <input
                      type="text"
                      className="form-control"
                      value={spec.value}
                      onChange={(e) => handleSpecificationChange(index, 'value', e.target.value)}
                      placeholder="e.g., 540W"
                    />
                  </div>
                  
                  <div className="info-item" style={{ display: 'flex', alignItems: 'end' }}>
                    {editData.specifications.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSpecification(index)}
                        className="btn btn-secondary"
                        style={{ fontSize: '12px', padding: '8px 12px', backgroundColor: '#dc3545', borderColor: '#dc3545', color: 'white' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))
            )
          ) : (
            <div className="order-info">
              {product.specifications && Object.entries(product.specifications).map(([key, value]) => (
                <div key={key} className="info-item">
                  <span className="info-label">{key}</span>
                  <span className="info-value">{value}</span>
                </div>
              ))}
              {(!product.specifications || Object.keys(product.specifications).length === 0) && (
                <div className="info-item">
                  <span className="info-value">No specifications available</span>
                </div>
              )}
            </div>
          )}
        </div>
        {isMobile && editing && (
          <div className="m-sticky-actions">
            <button
              onClick={handleCancel}
              disabled={saving}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-success"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;